<template>
  <div>
    <!-- Filters -->
    <v-layout row justify-end wrap class="mb-5">
      <v-flex xs12 md2>
        <div class="ml-3">
          <v-select
            color="primary"
            v-model="filters.contact_id"
            :items="[{ name: 'Mostrar todos', id: null }].concat(contactsList)"
            :label="$t('Contacto')"
            item-text="name"
            item-value="id"
            row
          ></v-select>
        </div>
      </v-flex>
      <v-flex xs12 md2>
        <div class="ml-3">
          <v-select
            color="primary"
            v-model="filters.month"
            item-text="text"
            item-value="value"
            :items="availableMonthsForQuarter"
            :clearable="true"
            row
            :label="$t('Mes del año')"
          ></v-select>
        </div>
      </v-flex>
      <v-flex xs12 md2>
        <div class="ml-3">
          <v-select
            v-if="invoiceType === 'factura'"
            color="primary"
            v-model="filters.status"
            item-text="text"
            item-value="value"
            :items="[
              { text: 'Todos los estados', value: null },
              { text: 'Pendiente', value: 'Pendiente' },
              { text: 'Cobrada', value: 'Cobrada' },
              { text: 'Impagada', value: 'Impagada' },
              { text: 'Rectificada', value: 'Rectificada' },
            ]"
            :clearable="true"
            row
            :label="$t('Estado')"
          ></v-select>
          <v-select
            v-else-if="invoiceType === 'presupuesto'"
            color="primary"
            v-model="filters.status"
            item-text="text"
            item-value="value"
            :items="[
              { text: 'Todos los estados', value: null },
              { text: 'Pendiente', value: 'Pendiente' },
              { text: 'Aceptado', value: 'Aceptado' },
              { text: 'Facturado', value: 'Facturado' },
              { text: 'Rechazado', value: 'Rechazado' },
            ]"
            :clearable="true"
            row
            :label="$t('Estado')"
          ></v-select>
        </div>
      </v-flex>
      <v-flex xs12 md2>
        <div class="ml-3">
          <v-select
            color="primary"
            v-model="filters.payment_method"
            item-text="text"
            item-value="value"
            :items="availablePaymentMethods"
            :clearable="true"
            row
            :label="$t('Forma de pago')"
          ></v-select>
        </div>
      </v-flex>
      <v-flex xs12 md4>
        <div class="ml-3">
          <v-text-field
            v-model="filters.search"
            append-icon="search"
            label="Buscar"
            single-line
            hide-details
          ></v-text-field>
        </div>
      </v-flex>
    </v-layout>
    <!-- Data table with invoices -->
    <v-data-table
      ref="openBillingInvoiceTable"
      v-model="selectedItems"
      :loading="loading"
      :headers="headers"
      :items="invoices"
      :pagination.sync="invoicesPagination"
      :rows-per-page-items="[10, 20, 30, 50, 100]"
      :total-items="invoicesPagination.totalItems"
      select-all
    >
      <template v-slot:no-data>
        <v-alert :value="true" color="info" icon="info" outline>
          {{ $t('No hay información que mostrar con los filtros seleccionados :(') }}
        </v-alert>
      </template>
      <template v-slot:headers="props">
        <tr>
          <th v-if="props.headers.length > 0">
            <v-checkbox
              v-model="selectAllRows"
              @change="selectAllTableRows()"
              primary
              hide-details
            ></v-checkbox>
          </th>
          <template v-for="header in props.headers">
            <th
              :key="`th_${header.value}`"
              :class="[
                'text-xs-center',
                header.sortable ? 'column sortable' : '',
                invoicesPagination.descending ? 'desc' : 'asc',
                header.value === invoicesPagination.sortBy ? 'active' : '',
              ]"
              @click="header.sortable ? changeSort(header.value) : null"
            >
              <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </template>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr :style="{ 'background-color': props.item.blocked ? '#eeeeee' : '' }">
          <td>
            <v-checkbox
              v-model="props.selected"
              @change="onSelectItem"
              primary
              hide-details
            ></v-checkbox>
          </td>
          <td v-for="header in headers" :key="header.value" class="text-xs-center">
            <template v-if="header.value === 'tax_base'">
              {{ props.item[header.value].toString().replace('.', ',') + '€' }}
            </template>
            <template v-else-if="header.value === 'total_amount'">
              {{ props.item[header.value].toString().replace('.', ',') + '€' }}
            </template>
            <template v-else-if="header.value === 'status' && props.item[header.value]">
              <!-- Invoices -->
              <v-edit-dialog
                v-if="invoiceType === 'factura'"
                class="row-editable"
                :return-value.sync="props.item[header.value]"
                large
                lazy
                persistent
                :save-text="'Guardar'"
                :cancel-text="'Cancelar'"
                @save="saveRow(props.item)"
                @cancel="cancelSaveRow(props.item)"
              >
                <v-chip :color="getColorForStatus(props.item[header.value])" text-color="white">
                  <strong>{{ props.item[header.value] }}</strong>
                </v-chip>
                <template v-slot:input>
                  <div class="mt-3">Estado</div>
                  <v-select
                    color="primary"
                    v-model="props.item[header.value]"
                    item-text="text"
                    item-value="value"
                    :items="[
                      { text: 'Pendiente', value: 'Pendiente' },
                      { text: 'Cobrada', value: 'Cobrada' },
                      { text: 'Impagada', value: 'Impagada' },
                    ]"
                    row
                  ></v-select>
                </template>
              </v-edit-dialog>
              <!-- Budgets -->
              <v-chip
                v-else
                :color="getColorForStatus(props.item[header.value])"
                text-color="white"
              >
                <strong>{{ props.item[header.value] }}</strong>
              </v-chip>
            </template>
            <template v-else-if="header.value === 'blocked' && props.item[header.value]">
              <v-tooltip top>
                <v-icon slot="activator" color="green">
                  mdi-check
                </v-icon>
                <span>
                  Bloqueada por gestor/a - {{ formatBlockedDateTime(props.item[header.value]) }}
                </span>
              </v-tooltip>
            </template>
            <template v-else-if="header.value === 'sent_to' && props.item[header.value]">
              <v-tooltip top>
                <v-icon slot="activator" color="green">
                  mdi-check
                </v-icon>
                <span v-for="(item, index) in props.item[header.value]" :key="`${index}-${uuid()}`">
                  Destinatario: {{ item['destiny'] }} - Fecha: {{ item['date'] }}
                  <br />
                </span>
              </v-tooltip>
            </template>
            <template v-else>
              {{ props.item[header.value] }}
            </template>
          </td>
          <td class="text-xs-center">
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark v-on="on">
                  <v-icon color="white">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-tile @click="editInvoice(props.item)">
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-pencil
                    </v-icon>
                    EDITAR
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="downloadPdf(props.item)">
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-download
                    </v-icon>
                    DESCARGAR PDF
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile v-if="invoiceType === 'factura'" @click="sendByEmail(props.item)">
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-email
                    </v-icon>
                    ENVIAR EMAIL
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile v-if="!props.item.rectify" @click="cloneInvoice(props.item)">
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-file-document-multiple
                    </v-icon>
                    CLONAR
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile v-if="!props.item.blocked" @click="deleteInvoice(props.item)">
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-delete
                    </v-icon>
                    ELIMINAR
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-if="!props.item.rectify && invoiceType === 'factura'"
                  @click="rectifyInvoice(props.item)"
                >
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-file-restore
                    </v-icon>
                    RECTIFICAR
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-if="invoiceType === 'presupuesto' && props.item.status !== 'Facturado'"
                  @click="convertBudgetToInvoice(props.item)"
                >
                  <v-list-tile-title>
                    <v-icon small class="mr-2">
                      mdi-file-document-multiple
                    </v-icon>
                    CREAR FACTURA
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
    </v-snackbar>
  </div>
</template>

<script>
const moment = require('moment');
require('moment/locale/es');
import apiCtrl from '@/services/api/abstract/abstract-api';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'OpenBillingInvoiceTable',
  components: {},
  props: ['year', 'quarter', 'invoiceType'],
  data() {
    return {
      invoices: [],
      selectedItems: [],
      selectAllRows: false,
      loading: false,
      headers: [],
      contactsList: [],
      availableMonthsForQuarter: [],
      availablePaymentMethods: [
        { text: 'No definido', value: 'No definido' },
        { text: 'Efectivo', value: 'Efectivo' },
        { text: 'Bizum', value: 'Bizum' },
        { text: 'Transferencia', value: 'Transferencia' },
        { text: 'Tarjeta bancaria', value: 'Tarjeta bancaria' },
        { text: 'Domiciliación bancaria', value: 'Domiciliación bancaria' },
        { text: 'Paypal', value: 'Paypal' },
      ],
      invoicesPagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10, // -1 for All",
        sortBy: '',
        totalItems: 0,
      },
      filters: {
        search: '',
        status: '',
        contact_id: null,
        year: this.year,
        quarter: this.quarter,
        month: null,
        payment_method: '',
      },
      sortTable: false,
      snack: false,
      snackText: '',
      snackColor: 'info',
    };
  },
  mounted() {
    this.getBillingInvoices();
    this.getContactsList();
    this.getAvailableMonthsForQuarter();
  },
  computed: {
    billingProfileId() {
      return this.$store.getters.currentOpenbillingProfileId;
    },
    clavedat() {
      return this.$store.state.clavedat;
    },
    currentPage() {
      return this.invoicesPagination.page;
    },
    perPage() {
      return this.invoicesPagination.rowsPerPage;
    },
    sortBy() {
      return this.invoicesPagination.sortBy;
    },
    descending() {
      return this.invoicesPagination.descending;
    },
    selectedYear() {
      return this.year;
    },
    selectedQuarter() {
      return this.quarter;
    },
    search() {
      return this.filters.search;
    },
    status() {
      return this.filters.status;
    },
    contactId() {
      return this.filters.contact_id;
    },
    month() {
      return this.filters.month;
    },
    paymentMethod() {
      return this.filters.payment_method;
    },
  },
  methods: {
    uuid() {
      return uuidv4();
    },
    getColorForStatus(status) {
      switch (status) {
        case 'Impagada':
          return '#FF3333';
        case 'Pendiente':
          return '#53BDFF';
        case 'Cobrada':
          return '#00CC33';
        case 'Aceptado':
          return '#53BDFF';
        case 'Facturado':
          return '#00CC33';
        case 'Rechazado':
          return '#FF3333';
        case 'Rectificada':
          return '#999999';
        default:
          return '#CCCCCC';
      }
    },
    getBillingInvoices() {
      this.loading = true;
      const config = {
        params: {
          page: this.invoicesPagination.page,
          per_page: this.invoicesPagination.rowsPerPage,
          sort_by: this.invoicesPagination.sortBy,
          descending: this.invoicesPagination.descending,
          search: this.search,
          status: this.status,
          contact_id: this.contactId,
          month: this.month,
          year: this.selectedYear,
          quarter: this.selectedQuarter,
          payment_method: this.paymentMethod,
          doc_type: this.invoiceType,
        },
      };
      apiCtrl
        .get(`/openbilling/${this.clavedat}/invoices/${this.billingProfileId}/list`, config)
        .then(res => {
          this.selectAllRows = false;
          this.selectAllTableRows();
          this.invoices = res.data.data;
          this.headers = res.data.dataTable.headers;
          this.invoicesPagination = {
            descending: this.invoicesPagination.descending,
            page: this.invoicesPagination.page,
            rowsPerPage: Number(res.data.meta.per_page), // -1 for All",
            sortBy: this.invoicesPagination.sortBy,
            totalItems: Number(res.data.meta.total),
          };
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getContactsList() {
      const config = {
        params: {
          page: 1,
          per_page: -1,
          sort_by: 'name',
          descending: 'asc',
        },
      };
      apiCtrl
        .get(`/openbilling/${this.clavedat}/contacts/${this.billingProfileId}/list`, config)
        .then(res => {
          this.contactsList = res.data.data;
        })
        .catch(() => {});
    },
    selectAllTableRows() {
      if (this.selectAllRows) {
        let selected = [];
        this.invoices.forEach(item => {
          selected.push(item);
        });
        this.selectedItems = selected;
      } else {
        this.selectedItems = [];
      }
    },
    changeSort(column) {
      if (this.invoicesPagination.sortBy === column) {
        this.invoicesPagination.descending = !this.invoicesPagination.descending;
      } else {
        this.invoicesPagination.sortBy = column;
        this.invoicesPagination.descending = false;
      }
    },
    editInvoice(invoice) {
      this.$emit('on-edit-invoice', invoice);
    },
    deleteInvoice(invoice) {
      this.$emit('on-delete-invoice', invoice);
    },
    rectifyInvoice(invoice) {
      this.$emit('on-rectify-invoice', invoice);
    },
    cloneInvoice(invoice) {
      this.$emit('on-clone-invoice', invoice);
    },
    getBillingInvoicesIfStopChanges() {
      this.loading = true;
      const params = {
        page: this.invoicesPagination.page,
        per_page: this.invoicesPagination.rowsPerPage,
        search: this.filters.search,
      };

      setTimeout(() => {
        if (
          params.page === this.invoicesPagination.page &&
          params.per_page === this.invoicesPagination.rowsPerPage &&
          params.search === this.filters.search
        ) {
          this.getBillingInvoices();
        }
      }, 300);
    },
    getAvailableMonthsForQuarter() {
      let months;
      switch (this.selectedQuarter) {
        case 1:
          months = [
            { text: 'Enero', value: 1 },
            { text: 'Febrero', value: 2 },
            { text: 'Marzo', value: 3 },
          ];
          break;
        case 2:
          months = [
            { text: 'Abril', value: 4 },
            { text: 'Mayo', value: 5 },
            { text: 'Junio', value: 6 },
          ];
          break;
        case 3:
          months = [
            { text: 'Julio', value: 7 },
            { text: 'Agosto', value: 8 },
            { text: 'Septiembre', value: 9 },
          ];
          break;
        case 4:
          months = [
            { text: 'Octubre', value: 10 },
            { text: 'Noviembre', value: 11 },
            { text: 'Diciembre', value: 12 },
          ];
          break;
        default:
          months = [
            { text: 'Enero', value: 1 },
            { text: 'Febrero', value: 2 },
            { text: 'Marzo', value: 3 },
            { text: 'Abril', value: 4 },
            { text: 'Mayo', value: 5 },
            { text: 'Junio', value: 6 },
            { text: 'Julio', value: 7 },
            { text: 'Agosto', value: 8 },
            { text: 'Septiembre', value: 9 },
            { text: 'Octubre', value: 10 },
            { text: 'Noviembre', value: 11 },
            { text: 'Diciembre', value: 12 },
          ];
      }
      this.availableMonthsForQuarter = months;
    },
    downloadPdf(invoice) {
      this.$emit('on-download-pdf', invoice);
    },
    convertBudgetToInvoice(invoice) {
      this.$emit('on-convert-budget-to-invoice', invoice);
    },
    saveRow(item) {
      // Update the row entity in backend
      apiCtrl
        .post(`/openbilling/${this.clavedat}/invoices/update/${item.id}`, item)
        .then(() => {
          this.showSnackMessage('success', 'Se ha actualizado el estado correctamente');
        })
        .catch(() => {
          this.showSnackMessage('error', 'Se ha producido un error al actualizar');
          this.getBillingInvoices();
        });
    },
    cancelSaveRow() {},
    showSnackMessage(action, message) {
      this.snackColor = action;
      this.snackText = message;
      this.snack = true;
    },
    formatBlockedDateTime(dateTime) {
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    },
    sendByEmail(invoice) {
      this.$emit('on-send-by-email', invoice);
    },
    onSelectItem(value) {
      if (!value && this.selectAllRows) {
        this.selectAllRows = false;
      }
    },
  },
  watch: {
    search: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    currentPage: function() {
      this.getBillingInvoicesIfStopChanges();
    },
    perPage: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    sortBy: function() {
      if (this.sortBy != null && this.sortTable) {
        this.getBillingInvoices();
      } else {
        if (this.descending === null) {
          this.sortTable = false;
        }
      }
    },
    descending: function() {
      if (this.descending != null) {
        this.getBillingInvoices();
        this.sortTable = true;
      } else {
        if (this.sortBy === null) {
          this.sortTable = false;
        }
      }
    },
    status: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    contactId: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    month: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    selectedYear: function() {
      this.filters.year = this.selectedYear;
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    selectedQuarter: function() {
      this.filters.month = null;
      this.filters.quarter = this.selectedQuarter;
      this.getAvailableMonthsForQuarter();
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
    selectedItems: function() {
      this.$emit('on-selected-items', this.selectedItems);
    },
    paymentMethod: function() {
      if (this.currentPage === 1) {
        this.getBillingInvoicesIfStopChanges();
      } else {
        this.invoicesPagination.page = 1;
      }
    },
  },
};
</script>

<style scoped>
.bg-disabled {
  background-color: #eeeeee !important;
}
.v-menu__activator {
  justify-content: center !important;
}
</style>
